import React, { useEffect, useRef, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import "../../../assets/css/gcadmin/managenotes/ManageNotesPage.css";

const NOTES = [
  { id: 1, title: "DSA Complete Notes",           subject: "Data Structures",            uploadedBy: "Ali Ahmed",    downloads: 234 },
  { id: 2, title: "OOP Concepts Summary",          subject: "Object Oriented Programming", uploadedBy: "Sara Khan",    downloads: 189 },
  { id: 3, title: "Database Normalization",        subject: "Database Systems",            uploadedBy: "Usman Tariq",  downloads: 156 },
  { id: 4, title: "Calculus Integration Techniques", subject: "Mathematics",              uploadedBy: "Fatima Noor",  downloads: 312 },
  { id: 5, title: "Physics Thermodynamics",        subject: "Physics",                    uploadedBy: "Hassan Ali",   downloads: 98  },
  { id: 6, title: "Web Development Basics",        subject: "Web Technologies",           uploadedBy: "Ayesha Malik", downloads: 267 },
];

const STATS = [
  { label: "Total Notes",      value: 6    },
  { label: "Total Downloads",  value: 1256 },
  { label: "Departments",      value: 3    },
  { label: "Contributors",     value: 6    },
];

/* ── Animated counter ── */
const Counter = ({ target, isVisible }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const step = Math.ceil(target / 50);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 18);
    return () => clearInterval(timer);
  }, [isVisible, target]);
  return <>{count.toLocaleString()}</>;
};

/* ── View Modal ── */
const ViewModal = ({ note, onClose }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 30); }, []);
  const handleClose = () => { setVisible(false); setTimeout(onClose, 260); };

  return (
    <div className={`gcAdmin-mn-backdrop ${visible ? "gcAdmin-mn-backdrop--in" : ""}`} onClick={handleClose}>
      <div className={`gcAdmin-mn-modal ${visible ? "gcAdmin-mn-modal--in" : ""}`} onClick={e => e.stopPropagation()}>
        <div className="gcAdmin-mn-modal-header">
          <h5 className="gcAdmin-mn-modal-title">Note Details</h5>
          <button className="gcAdmin-mn-modal-close" onClick={handleClose}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        <div className="gcAdmin-mn-modal-body">
          {[
            ["Title",       note.title],
            ["Subject",     note.subject],
            ["Uploaded By", note.uploadedBy],
            ["Downloads",   note.downloads],
          ].map(([label, value]) => (
            <div className="gcAdmin-mn-detail-row" key={label}>
              <span className="gcAdmin-mn-detail-label">{label}</span>
              <span className="gcAdmin-mn-detail-value">{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Delete Confirm Modal ── */
const DeleteModal = ({ note, onClose, onConfirm }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 30); }, []);
  const handleClose = () => { setVisible(false); setTimeout(onClose, 260); };

  return (
    <div className={`gcAdmin-mn-backdrop ${visible ? "gcAdmin-mn-backdrop--in" : ""}`} onClick={handleClose}>
      <div className={`gcAdmin-mn-modal gcAdmin-mn-modal--sm ${visible ? "gcAdmin-mn-modal--in" : ""}`} onClick={e => e.stopPropagation()}>
        <div className="gcAdmin-mn-delete-icon">
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
            <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="#e03e3e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h5 className="gcAdmin-mn-delete-title">Delete Note?</h5>
        <p className="gcAdmin-mn-delete-msg">
          Are you sure you want to delete <strong>"{note.title}"</strong>? This cannot be undone.
        </p>
        <div className="gcAdmin-mn-delete-actions">
          <button className="gcAdmin-mn-delete-cancel" onClick={handleClose}>Cancel</button>
          <button className="gcAdmin-mn-delete-confirm" onClick={() => { onConfirm(note.id); handleClose(); }}>Delete</button>
        </div>
      </div>
    </div>
  );
};

/* ── Main Page ── */
const ManageNotesPage = () => {
  const [notes, setNotes]         = useState(NOTES);
  const [viewNote, setViewNote]   = useState(null);
  const [deleteNote, setDeleteNote] = useState(null);
  const [pageVisible, setPageVisible] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const [rowVisible, setRowVisible]   = useState([]);
  const statsRef = useRef(null);

  useEffect(() => {
    setTimeout(() => setPageVisible(true), 60);
    // Stagger rows
    notes.forEach((_, i) => {
      setTimeout(() => {
        setRowVisible(prev => { const n = [...prev]; n[i] = true; return n; });
      }, 200 + i * 65);
    });
  }, []);

  // Stats intersection
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.2 }
    );
    if (statsRef.current) obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, []);

  const handleDelete = (id) => setNotes(prev => prev.filter(n => n.id !== id));

  return (
    <div className={`gcAdmin-mn-page ${pageVisible ? "gcAdmin-mn-page--visible" : ""}`}>
      <Container fluid className="gcAdmin-mn-container">

        {/* ── Title ── */}
        <h2 className="gcAdmin-mn-title">Manage Notes</h2>

        {/* ── Stat Cards ── */}
        <Row className="gy-3 gx-3 mb-4" ref={statsRef}>
          {STATS.map((stat, i) => (
            <Col key={stat.label} xs={12} sm={6} lg={3}>
              <div
                className={`gcAdmin-mn-stat ${statsVisible ? "gcAdmin-mn-stat--visible" : ""}`}
                style={{ transitionDelay: `${i * 0.09}s` }}
              >
                <div className="gcAdmin-mn-stat-value">
                  <Counter target={stat.value} isVisible={statsVisible} />
                </div>
                <div className="gcAdmin-mn-stat-label">{stat.label}</div>
              </div>
            </Col>
          ))}
        </Row>

        {/* ── Table ── */}
        <div className="gcAdmin-mn-table-wrap">
          <table className="gcAdmin-mn-table">
            <thead>
              <tr>
                <th className="gcAdmin-mn-th">Title</th>
                <th className="gcAdmin-mn-th">Subject</th>
                <th className="gcAdmin-mn-th">Uploaded By</th>
                <th className="gcAdmin-mn-th">Downloads</th>
                <th className="gcAdmin-mn-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {notes.map((note, i) => (
                <tr
                  key={note.id}
                  className={`gcAdmin-mn-tr ${rowVisible[i] ? "gcAdmin-mn-tr--visible" : ""}`}
                  style={{ transitionDelay: `${i * 0.05}s` }}
                >
                  <td className="gcAdmin-mn-td gcAdmin-mn-td--title">{note.title}</td>
                  <td className="gcAdmin-mn-td gcAdmin-mn-td--subject">{note.subject}</td>
                  <td className="gcAdmin-mn-td gcAdmin-mn-td--author">{note.uploadedBy}</td>
                  <td className="gcAdmin-mn-td gcAdmin-mn-td--downloads">{note.downloads}</td>
                  <td className="gcAdmin-mn-td">
                    <div className="gcAdmin-mn-actions">
                      {/* Eye */}
                      <button
                        className="gcAdmin-mn-btn gcAdmin-mn-btn--view"
                        onClick={() => setViewNote(note)}
                        title="View"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z" stroke="currentColor" strokeWidth="1.8"/>
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8"/>
                        </svg>
                      </button>

                      {/* Approve / Check */}
                      <button className="gcAdmin-mn-btn gcAdmin-mn-btn--approve" title="Approve">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>

                      {/* Delete */}
                      <button
                        className="gcAdmin-mn-btn gcAdmin-mn-btn--delete"
                        onClick={() => setDeleteNote(note)}
                        title="Delete"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                          <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {notes.length === 0 && (
                <tr>
                  <td colSpan={5} className="gcAdmin-mn-td gcAdmin-mn-td--empty">No notes found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </Container>

      {viewNote   && <ViewModal   note={viewNote}   onClose={() => setViewNote(null)} />}
      {deleteNote && <DeleteModal note={deleteNote} onClose={() => setDeleteNote(null)} onConfirm={handleDelete} />}
    </div>
  );
};

export default ManageNotesPage;